import { useEffect, useState, useRef } from "react";
import { WifiOff, X } from "lucide-react";

/**
 * <NetworkOfflineToast /> — top-of-screen banner shown when axios sees
 * repeated network failures inside a short window (3 errors in 30s).
 *
 * Listens for two CustomEvents emitted by `lib/errorLogger.js`:
 *   - "realix:network-down"  → show banner
 *   - "realix:network-up"    → hide banner
 *
 * Auto-hides after AUTO_HIDE_MS as a fallback in case the recovery event
 * never fires (e.g. user closes the laptop). Does NOT log any error itself
 * — purely a UX surface.
 *
 * Mount this once at the App root, NOT per-route. Position fixed top so it
 * doesn't reflow page content.
 */

const AUTO_HIDE_MS = 30_000;

export default function NetworkOfflineToast() {
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const onDown = () => {
      setVisible(true);
      // Reset the auto-hide timer every time we re-trigger
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    };
    const onUp = () => {
      setVisible(false);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
    window.addEventListener("realix:network-down", onDown);
    window.addEventListener("realix:network-up", onUp);
    return () => {
      window.removeEventListener("realix:network-down", onDown);
      window.removeEventListener("realix:network-up", onUp);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[200] pointer-events-none flex justify-center px-3 pt-3"
      role="status"
      aria-live="polite"
      data-testid="network-offline-toast"
    >
      <div
        className="pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-lg shadow-xl bg-orange-500/95 text-white text-sm font-medium border border-orange-400/50 backdrop-blur"
      >
        <WifiOff className="w-4 h-4 flex-shrink-0" />
        <span data-testid="network-offline-toast-message">
          Connection issue detected. Some data may not load. Check your
          internet connection or try again.
        </span>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className="ml-2 -mr-1 p-1 rounded hover:bg-white/15 transition-colors"
          aria-label="Dismiss"
          data-testid="network-offline-toast-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
